import { ChangeEvent, FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
// Types
import { ContentType } from 'app/types/ContentItem';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { mergeTemplate } from 'app/store/Templates/Templates.async';
// Selectors
import { selectAllTemplatesAsOptions } from 'app/store/Templates/Templates.selectors';
import { selectCustomDocumentsAsOptions } from 'app/store/DMSDocuments/DMSDocuments.selectors';
// Mui
import {
  Radio, RadioGroup, FormControlLabel, FormControl
} from '@mui/material';
// Components
import Dialog from 'app/components/Dialog';
import { Button } from 'app/components/Mui';
import { Autocomplete } from 'app/components/Mui/Autocompletes';
// Utils
import { generateContentItem } from 'app/utilities/ContentItemUtils';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (content:string) => void;
}

const DocumentTemplateDialog:FC<Props> = ({
  // Props
  open, onClose, onConfirm
}) => {
  const { caseId } = useParams<{ caseId:string }>();

  const { t } = useTranslation();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const templatesOptions = useAppSelector(selectAllTemplatesAsOptions);
  const documentsOptions = useAppSelector(selectCustomDocumentsAsOptions);

  const [ value, setValue ] = useState<'blank' | 'templates' | 'documents'>('blank');
  const [ templatesIds, setTemplatesIds ] = useState<number[]>([]);
  const [ documentsIds, setDocumentsIds ] = useState<number[]>([]);
  const [ loading, setLoading ] = useState(false);

  const handleClick = () => {
    if ( value === 'blank' ){
      onConfirm('');
      onClose();
    } else {
      if ( value === 'templates' && !templatesIds.length ) return;
      if ( value === 'documents' && !documentsIds.length ) return;
      asyncMergeTemplate();
    }
  }

  const asyncMergeTemplate = async () => {
    const mergeTemplateContent = value === 'templates'
      ? templatesIds.map((templateId) => generateContentItem(ContentType.Template, templateId))
      : documentsIds.map((documentId) => generateContentItem(ContentType.Document, documentId));

    setLoading(true);
    try {
      const { content } = await dispatch(mergeTemplate({
        caseId: Number(caseId),
        content: mergeTemplateContent
      })).unwrap();
      onConfirm(content);
      onClose();
    } catch(error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value as any);
    setTemplatesIds([]);
    setDocumentsIds([]);
  };

  const handleTemplatesChange = (value:any) => {
    setTemplatesIds(value);
  }

  const handleDocumentsChange = (value:any) => {
    setDocumentsIds(value);
  }

  const isButtonDisabled = (value === 'templates' && !templatesIds.length) || (value === 'documents' && !documentsIds.length);

  const actions = (
    <Fragment>
      <Button
        name="Cancel doucment template dialog"
        onClick={onClose}
      >{t('buttons.cancel')}</Button>
      <Button
        name="Use document template dialog"
        loading={loading}
        disabled={isButtonDisabled}
        onClick={handleClick}
        color="primary"
        variant="contained"
      >{t('buttons.next')}</Button>
    </Fragment>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('dialogs.documentTemplate.title')}
      actions={actions}
    >
      <FormControl>
        <RadioGroup
          value={value}
          onChange={handleChange}
        >
          <FormControlLabel value="blank" control={<Radio />} label={t('dialogs.documentTemplate.blankDocument')} />
          <FormControlLabel value="templates" control={<Radio />} label={t('dialogs.documentTemplate.fromTemplates')} />
          <FormControlLabel value="documents" control={<Radio />} label={t('dialogs.documentTemplate.fromExistingDocuments')} />
        </RadioGroup>
      </FormControl>
      {value === 'templates' ? (
        <Autocomplete
          multiple
          value={templatesIds}
          onChange={handleTemplatesChange}
          options={templatesOptions || []}
          TextFieldProps={{
            label: t('dialogs.documentTemplate.selectTemplates')
          }}
        />
      ) : null}
      {value === 'documents' ? (
        <Autocomplete
          multiple
          value={documentsIds}
          onChange={handleDocumentsChange}
          options={documentsOptions || []}
          TextFieldProps={{
            label: t('dialogs.documentTemplate.selectDocuments')
          }}
        />
      ) : null}
    </Dialog>
  )
}

export default DocumentTemplateDialog;
